import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import { type DayGroup } from '@/shared/config';
import { groupByDay } from '@/shared/lib';

import { type TranscriptorConsultation } from '@/entities/transcriptor';

import {
  TIMELINE_CATEGORIES,
  TIMELINE_CATEGORY_BY_REPORT_TYPE,
  type TimelineCategory,
  type TimelineItem,
  type UnifiedItem,
} from '../config';

const getConsultationTime = (consultation: TranscriptorConsultation) =>
  consultation.date instanceof Date
    ? consultation.date.getTime()
    : new Date(consultation.date).getTime();

export const getTimelineItems = ({
  items,
  recordings = [],
  albums = [],
}: {
  items: UnifiedItem[];
  recordings?: TranscriptorConsultation[];
  albums?: DentalPhotoAlbum[];
}): TimelineItem[] =>
  [
    ...items,
    ...recordings.map<TimelineItem>((consultation) => ({
      type: 'transcript',
      data: consultation,
      date: getConsultationTime(consultation),
    })),
    ...albums.map<TimelineItem>((album) => ({
      type: 'photoAlbum',
      data: album,
      date: album.Time?.toDate().getTime() ?? 0,
    })),
  ].sort((a, b) => b.date - a.date);

export const getTimelineItemKey = (item: TimelineItem): string =>
  item.type === 'transcript' ? item.data.id : item.data.ID;

export const getTimelineCategory = (item: TimelineItem): TimelineCategory => {
  switch (item.type) {
    case 'transcript':
      return 'transcripts';
    case 'photoAlbum':
      return 'photos';
    case 'rawMeshViewer':
      return '3d';
    default:
      return TIMELINE_CATEGORY_BY_REPORT_TYPE[item.data.reportType];
  }
};

export const filterTimelineItems = (
  items: TimelineItem[],
  category: TimelineCategory,
): TimelineItem[] =>
  category === 'all'
    ? items
    : items.filter((item) => getTimelineCategory(item) === category);

// The photos filter shows a gallery, so its chip counts photos rather than
// albums; every other category counts timeline items.
const countCategoryItems = (items: TimelineItem[]): number =>
  items.reduce(
    (count, item) =>
      count + (item.type === 'photoAlbum' ? item.data.StudyAssetIDs.length : 1),
    0,
  );

export const countTimelineItemsByCategory = (
  items: TimelineItem[],
): Record<TimelineCategory, number> =>
  TIMELINE_CATEGORIES.reduce(
    (counts, category) => ({
      ...counts,
      [category]:
        category === 'photos'
          ? countCategoryItems(filterTimelineItems(items, category))
          : filterTimelineItems(items, category).length,
    }),
    {} as Record<TimelineCategory, number>,
  );

export const groupTimelineItemsByDay = (
  items: TimelineItem[],
): DayGroup<TimelineItem>[] => groupByDay(items, (item) => new Date(item.date));
